import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Button } from '../components/UI';
import Logo from '../components/Logo';
import MobileMenu from '../components/MobileMenu';

const HomePage = () => {
  const { language, changeLanguage } = useLanguage();
  const t = (key) => getTranslation(language, key);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Logo size="medium" />
            <span className="text-xl font-bold">{t('appName')}</span>
          </div>
          
          {/* Navigation */}
          <div className="flex items-center space-x-6">
            {/* Desktop Links */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/about" className="text-gray-300 hover:text-white transition">
                {t('aboutUs')}
              </Link>
              <Link to="/interes" className="text-gray-300 hover:text-white transition">
                {t('interestRegistration')}
              </Link>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center space-x-1 bg-purple-800 rounded-lg px-2 py-1">
              {['es', 'en', 'pt'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={`px-2 py-1 rounded text-sm font-medium transition ${
                    language === lang
                      ? 'bg-yellow-500 text-black'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            
            {/* Mobile Hamburger Menu */}
            <MobileMenu />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-4 py-20">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
            {t('landingTitle')}
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed max-w-4xl mx-auto">
            {t('landingDescription')}
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-8">
            <Link to="/interes">
              <Button className="bg-yellow-500 hover:bg-yellow-600 text-black text-xl px-10 py-5 font-bold w-full sm:w-auto shadow-2xl">
                {t('registerForInvitation')} →
              </Button>
            </Link>
          </div>
          
          <p className="text-2xl text-yellow-300 font-semibold">
            {t('availableSoonChile')}
          </p>
          
          <p className="text-sm text-gray-300 mt-3">
            {t('sdgStatement')}
          </p>

          {/* Features - Reordered: Technicians, AI, Clinics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-24">
            {/* 1. Técnicos Veterinarios */}
            <Link to="/for-technicians" className="bg-gradient-to-br from-purple-800 to-purple-900 border border-purple-600 rounded-lg p-8 backdrop-blur hover:border-yellow-500 transition-all transform hover:scale-105 cursor-pointer shadow-xl hover:shadow-2xl">
              <div className="w-16 h-16 mb-4 bg-yellow-500 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-yellow-400">
                {language === 'es' ? 'Técnicos Veterinarios' : language === 'en' ? 'Veterinary Technicians' : 'Técnicos Veterinários'}
              </h3>
              <p className="text-gray-200 leading-relaxed">
                {language === 'es' ? 'Trabaja con flexibilidad y transparencia' : language === 'en' ? 'Work with flexibility and transparency' : 'Trabalhe com flexibilidade e transparência'}
              </p>
            </Link>

            {/* 2. IA & Tecnología */}
            <Link to="/ai-powered" className="bg-gradient-to-br from-purple-800 to-purple-900 border border-purple-600 rounded-lg p-8 backdrop-blur hover:border-yellow-500 transition-all transform hover:scale-105 cursor-pointer shadow-xl hover:shadow-2xl">
              <div className="w-16 h-16 mb-4 bg-yellow-500 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-yellow-400">
                {language === 'es' ? 'IA & Tecnología' : language === 'en' ? 'AI & Technology' : 'IA & Tecnologia'}
              </h3>
              <p className="text-gray-200 leading-relaxed">
                {language === 'es' ? 'Emparejamiento inteligente y automatización' : language === 'en' ? 'Smart matching and automation' : 'Correspondência inteligente e automação'}
              </p>
            </Link>

            {/* 3. Clínicas Veterinarias */}
            <Link to="/for-clinics" className="bg-gradient-to-br from-purple-800 to-purple-900 border border-purple-600 rounded-lg p-8 backdrop-blur hover:border-yellow-500 transition-all transform hover:scale-105 cursor-pointer shadow-xl hover:shadow-2xl">
              <div className="w-16 h-16 mb-4 bg-yellow-500 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-purple-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-3 text-yellow-400">
                {language === 'es' ? 'Clínicas Veterinarias' : language === 'en' ? 'Veterinary Clinics' : 'Clínicas Veterinárias'}
              </h3>
              <p className="text-gray-200 leading-relaxed">
                {language === 'es' ? 'Encuentra técnicos calificados rápidamente' : language === 'en' ? 'Find qualified technicians quickly' : 'Encontre técnicos qualificados rapidamente'}
              </p>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-purple-700 mt-16 bg-purple-950 bg-opacity-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Logo & Copyright */}
            <div className="flex flex-col items-center md:items-start">
              <div className="flex items-center space-x-3 mb-4">
                <Logo size="small" />
                <span className="font-bold text-lg">{t('appName')}</span>
              </div>
              <p className="text-gray-400 text-sm text-center md:text-left">
                {t('copyright')}
              </p>
              <p className="text-gray-500 text-xs mt-2 text-center md:text-left">
                {t('sdgFooterText')}
              </p>
            </div>
            
            {/* Links */}
            <div className="flex flex-col items-center md:items-start space-y-3">
              <h4 className="font-semibold text-yellow-400 mb-2">
                {language === 'es' ? 'Enlaces' : language === 'en' ? 'Links' : 'Links'}
              </h4>
              <Link to="/about" className="text-gray-400 hover:text-white transition text-sm">
                {t('aboutUs')}
              </Link>
              <Link to="/interes" className="text-gray-400 hover:text-white transition text-sm">
                {t('interestRegistration')}
              </Link>
              <Link to="/innovacion-y-hoja-de-ruta" className="text-gray-400 hover:text-white transition text-sm">
                {language === 'es' ? 'Innovación & Roadmap' : language === 'en' ? 'Innovation & Roadmap' : 'Inovação & Roadmap'}
              </Link>
              <Link to="/politica-de-privacidad" className="text-gray-400 hover:text-white transition text-sm">
                {t('privacyPolicy')}
              </Link>
            </div>
            
            {/* Social Icons */}
            <div className="flex flex-col items-center md:items-start">
              <h4 className="font-semibold text-yellow-400 mb-4">
                {language === 'es' ? 'Síguenos' : language === 'en' ? 'Follow Us' : 'Siga-nos'}
              </h4>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-500 hover:text-gray-400 transition cursor-not-allowed" title="Coming soon" onClick={(e) => e.preventDefault()}>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-500 hover:text-gray-400 transition cursor-not-allowed" title="Coming soon" onClick={(e) => e.preventDefault()}>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-500 hover:text-gray-400 transition cursor-not-allowed" title="Coming soon" onClick={(e) => e.preventDefault()}>
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
